import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Fetches and morphs Shopify sections while deduplicating concurrent requests.
 * VELUNE PERFORMANCE V2: resilient pending-request cleanup + complete page cache warmup.
 */
class SectionRenderer {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {Map<string, AbortController>} */
  #abortControllersBySectionId = new Map();

  /** @type {Map<string, Promise<string>>} */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections, { once: true });
  }

  /**
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @param {'hydration'|'full'} [options.mode]
   * @param {URL} [options.url]
   * @returns {Promise<string>}
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full', url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        morphSection(sectionId, sectionHTML, { mode });
      }

      return sectionHTML;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  /** @param {string} sectionId */
  #abortPendingMorph(sectionId) {
    this.#abortControllersBySectionId.get(sectionId)?.abort();
  }

  /**
   * @param {string} sectionId
   * @param {boolean} useCache
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    const existingPromise = this.#pendingPromises.get(sectionUrl);
    if (existingPromise) return existingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    const pendingPromise = fetch(sectionUrl, {
      headers: { Accept: 'text/html' },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Section request failed (${response.status}): ${sectionUrl}`);
      }
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    try {
      const sectionHTML = await pendingPromise;
      this.#cache.set(sectionUrl, sectionHTML);
      return sectionHTML;
    } finally {
      // A rejected request must never remain permanently stuck in the pending map.
      if (this.#pendingPromises.get(sectionUrl) === pendingPromise) {
        this.#pendingPromises.delete(sectionUrl);
      }
    }
  }

  #cachePageSections = () => {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);

      // Continue through the entire page. The previous `return` stopped the
      // cache warmup at the first cached/shadow-root section.
      if (this.#cache.has(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  };
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * @param {string} sectionId
 * @param {URL} url
 * @returns {string}
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  const nextUrl = new URL(url.toString());
  nextUrl.searchParams.set('section_id', normalizeSectionId(sectionId));
  nextUrl.searchParams.sort();
  return nextUrl.toString();
}

/** @param {string} sectionId */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/** @param {string} sectionId */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/** @param {Element} element */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @param {string} sectionId
 * @param {string} html
 * @param {Object} [options]
 * @param {'hydration'|'full'} [options.mode]
 * @param {boolean} [options.injectStylesheet]
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) throw new Error(`Section ${sectionId} not found`);
  if (!newElement) throw new Error(`Section ${sectionId} not found in the section rendering response`);

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  if (injectStylesheet) injectSectionStylesheet(fragment, existingElement);
}

/**
 * @param {Document} fragment
 * @param {HTMLElement} sectionElement
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
